import { HttpClient } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { AddressChooserComponent } from "@app/address/components/address-chooser/address-chooser.component";
import { Action } from "@ngrx/store";
import { Observable, of } from "rxjs";
import { catchError, map, take, tap } from "rxjs/operators";
import { Address } from "../model/address";

export interface LookupAddressParams {
    address?: string;
    address2?: string;
    city?: string;
    zipcode?: string;
    country?: string;
}

@Injectable({
    providedIn: "root",
})
export class AddressUtilityService {
    private readonly stateAbbreviations: { [key: string]: string } = {
        Alabama: "AL",
        Alaska: "AK",
        Arizona: "AZ",
        Arkansas: "AR",
        California: "CA",
        Colorado: "CO",
        Connecticut: "CT",
        Delaware: "DE",
        Florida: "FL",
        Georgia: "GA",
        Hawaii: "HI",
        Idaho: "ID",
        Illinois: "IL",
        Indiana: "IN",
        Iowa: "IA",
        Kansas: "KS",
        Kentucky: "KY",
        Louisiana: "LA",
        Maine: "ME",
        Maryland: "MD",
        Massachusetts: "MA",
        Michigan: "MI",
        Minnesota: "MN",
        Mississippi: "MS",
        Missouri: "MO",
        Montana: "MT",
        Nebraska: "NE",
        Nevada: "NV",
        "New Hampshire": "NH",
        "New Jersey": "NJ",
        "New Mexico": "NM",
        "New York": "NY",
        "North Carolina": "NC",
        "North Dakota": "ND",
        Ohio: "OH",
        Oklahoma: "OK",
        Oregon: "OR",
        Pennsylvania: "PA",
        "Rhode Island": "RI",
        "South Carolina": "SC",
        "South Dakota": "SD",
        Tennessee: "TN",
        Texas: "TX",
        Utah: "UT",
        Vermont: "VT",
        Virginia: "VA",
        Washington: "WA",
        "West Virginia": "WV",
        Wisconsin: "WI",
        Wyoming: "WY",
    };
    constructor(private readonly httpClient: HttpClient) {}

    subscribeToInnerEmitter(emitter: EventEmitter<Action[]>, dialogRef: MatDialogRef<AddressChooserComponent>) {
        const emitterSubscription = emitter.subscribe(() => {
            dialogRef.close();
            emitterSubscription.unsubscribe();
        });
    }

    lookupAddress(params: LookupAddressParams): Observable<any> {
        const { address, address2, city, zipcode, country } = params;
        // Make sure we don't have any null parameters.
        const _address = !address ? "" : encodeURI(address).replace("#", "%23"); // .replace(/ /g, "+")
        const _address2 = !address2 ? "" : encodeURI(address2).replace("#", "%23");
        const _city = !city ? "" : encodeURI(city);
        const _zipcode = !zipcode ? "" : encodeURI(zipcode);
        const _country = !country ? "" : encodeURI(country);
        const encodedAddress = `${_address}+${_address2}+${_city}+${_country}+${_zipcode}`;
        // Get the geocoded address info, based on what was provided.
        return this.geocodeAddress(encodedAddress).pipe(
            map(results => this.parseAddresses(results)),
            tap((addresses: Address[]) => {
                addresses.forEach((addr, index) => (addr.id = index));
            })
        );
    }

    extractAddressFromGeoResults(addressComp: any, addr: Address) {
        const streetParts = [];
        let streetParts2 = [];
        const postalcodeParts = [];
        // loop over the terrible results from Google
        addressComp.map(component => {
            // assign each component value to its appropriate address key
            switch (component.types[0]) {
                case "street_number":
                    streetParts.unshift(component.long_name);
                    break;

                case "route":
                    streetParts.push(component.long_name);
                    break;

                case "subpremise":
                    streetParts2.push(component.long_name);
                    break;

                case "premise":
                    streetParts2.unshift(component.long_name);
                    break;

                case "country":
                    addr.country = component.long_name;
                    break;

                case "administrative_area_level_1":
                    addr.state = component.long_name;
                    break;

                case "administrative_area_level_3":
                case "locality":
                case "postal_town":
                    if (!addr.city) {
                        addr.city = component.long_name;
                    }
                    break;

                case "postal_code":
                    postalcodeParts.push(component.long_name);
                    break;

                case "postal_code_suffix":
                    postalcodeParts.push(component.long_name);
                    break;
            }
        });

        streetParts2 = streetParts2.map(streetPart => {
            if ((+streetPart).toString() === streetPart) {
                return "#" + streetPart;
            }

            return streetPart;
        });

        // combine the separated parts to the keys on Address
        addr.address = streetParts.join(" ");
        addr.address2 = streetParts2.join(" ");
        addr.zipcode = postalcodeParts.join("-");
    }

    parseAddresses(result): Address[] {
        const addresses: Address[] = [];
        if (result?.results) {
            for (const entry of result.results) {
                const latitude = entry.geometry.location.lat;
                const longitude = entry.geometry.location.lng;
                const addr = {
                    coordinates: `${latitude}, ${longitude}`,
                } as Address;
                // Get the timezone
                this.timezoneFromCoordinates(addr).pipe(take(1)).subscribe();
                this.extractAddressFromGeoResults(entry.address_components, addr);
                addr.formattedAddress = entry.formatted_address;
                addresses.push(addr);
            }
        }

        return addresses;
    }

    geocodeAddress(address) {
        return this.httpClient
            .get(`https://maps.googleapis.com/maps/api/geocode/json?address=${address}&key=AIzaSyDe1-NGcxFDMb2NBIts1-MRbViV0MMZkKI`)
            .pipe(
                map(result => result || {}),
                catchError(e => of({}))
            );
    }

    timezoneFromCoordinates(address: Address) {
        const coords = address?.coordinates?.replace(" ", "");

        if (coords) {
            const now = Date.now() / 1000;
            const url =
                "https://maps.googleapis.com/maps/api/timezone/json?location=" +
                coords +
                "&timestamp=" +
                now +
                "&key=AIzaSyDe1-NGcxFDMb2NBIts1-MRbViV0MMZkKI";
            return this.httpClient.get(url).pipe(
                tap((_result: any) => {
                    if (_result?.timeZoneId) {
                        address.timezonename = _result.timeZoneName;
                        address.timezoneid = _result.timeZoneId;
                    }
                }),
                catchError(e => of(e))
            );
        }

        return of(null);
    }

    getStateAbbreviation(stateName: string): string {
        const lowerCaseStateName = stateName?.toLowerCase();
        const lowerCaseStateAbbreviations = Object.keys(this.stateAbbreviations).reduce((acc, key) => {
            acc[key.toLowerCase()] = this.stateAbbreviations[key];
            return acc;
        }, {} as { [key: string]: string });
        return lowerCaseStateAbbreviations[lowerCaseStateName] || "Invalid state name";
    }
}
